import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Routes } from 'src/Routes.constants';
import { CopyableLink } from 'common/components/Table/Link';
import { RSimInventorySimulateOutage } from './RSimInventorySimulateOutage';
import { RSimInventoryPollingSimStatus } from './RSimInventoryPollingSimStatus';
import { SectionGridRow } from '@/common/components/SectionGrid/SectionGridRow';
import { SectionGridItem } from '@/common/components/SectionGrid/SectionGridItem';
import {
  rSimInventorySimDetailsGridMediumScreen,
  rSimInventorySimDetailsGridSmallScreen,
  simGridSizes,
  tagGridSizes,
} from './RSimInventoryDetailsPage.style';
import { TagDisplay } from 'tags/TagsDisplay';
import { Tag } from 'tags/models/tagsApi.types';

import Box from '@mui/material/Box';
import { palette } from 'theme/palette';
import { PrimarySimGrid } from './components/PrimarySimGrid';
import { SecondarySimGrid } from './components/SecondarySimGrid';
import { styled, useTheme } from '@mui/material/styles';
import { SimDetails } from '@/common/components/Sim/Details/SimDetails';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { Actions, Subjects } from 'permissions/ability';
import { ErrorFallback } from 'common/components/Fallbacks/ErrorFallback';
import { SimProfileTagProvider } from 'simInventory/Tags/SimProfileTagProvider';
import { RsimProfileStatus } from '../RSimApi/rSimApi.interface';
import { mapSimDTO } from 'src/common/components/Sim/Inventory/utils/simListResponseParsers';
import { useSimDetails } from 'src/simInventory/hooks/useSimDetails';
import useMediaQuery from '@mui/material/useMediaQuery';

type RSimInventorySimDetailsProps = {
  primarySimEntry: ReturnType<typeof mapSimDTO> | null;
  secondarySimEntry: ReturnType<typeof mapSimDTO> | null;
  rSimStatus: RsimProfileStatus;
  euicc: string;
};

type SimProfileDetailsLoaderProps = {
  iccid: string;
  rSimViewSimProfileRow: React.ReactNode;
};

const SimProfileDetailsLoader = ({ iccid, ...props }: SimProfileDetailsLoaderProps) => {
  const { data } = useSimDetails(iccid);
  return <SimDetails baseRoute={Routes.simInventory} {...props} data={data!} rSimView />;
};

const SimProfileTitle = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(8),
}));

export const RSimInventorySimDetails: React.FC<RSimInventorySimDetailsProps> = ({
  primarySimEntry,
  secondarySimEntry,
  rSimStatus,
  euicc,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const matchesMd = useMediaQuery('(min-width:900px)');

  return (
    <SimProfileTagProvider>
      <Box
        sx={{
          ...rSimInventorySimDetailsGridSmallScreen,
          [theme.breakpoints.up('md')]: {
            ...rSimInventorySimDetailsGridMediumScreen,
          },
          borderTop: `1px solid ${palette.border}`,
        }}
      >
        <div
          style={{
            gridArea: 'divider',
            borderRight: `1px solid ${palette.border}`,
          }}
        ></div>

        <PrimarySimGrid sx={{ gridArea: 'primary1' }} hideTopBorder={true}>
          {primarySimEntry?.iccid && primarySimEntry.iccid !== '-' ? (
            <SimProfileTitle>
              <Typography variant="h3" color="text.secondary">
                {t('common.primary')}
              </Typography>
              <CopyableLink
                value={primarySimEntry?.iccid}
                to={`${Routes.simInventory}/${primarySimEntry?.iccid}`}
              />
              <SimProfileDetailsLoader
                iccid={primarySimEntry?.iccid}
                data-testid={'primarySimInventory'}
                rSimViewSimProfileRow={
                  <>
                    <SectionGridRow hideTopBorder={true} title={t('simInventory.simProfile')}>
                      <>
                        <SectionGridItem
                          {...simGridSizes}
                          label={t('simInventory.profileStatus')}
                          testId={`profileStatus`}
                        >
                          <RSimInventoryPollingSimStatus
                            euicc={euicc}
                            isPrimary={true}
                            rSimStatus={rSimStatus}
                          />
                        </SectionGridItem>
                        <SimInventoryCan I={Actions.simulate} a={Subjects.outage}>
                          <SectionGridItem
                            {...simGridSizes}
                            label={t('common.simulateOutage')}
                            testId={`simulateOutage`}
                          >
                            <RSimInventorySimulateOutage euicc={euicc} isPrimary={true} />
                          </SectionGridItem>
                        </SimInventoryCan>
                      </>
                    </SectionGridRow>
                    <SectionGridRow
                      hideTopBorder={true}
                      sx={{
                        pb: 12,
                      }}
                    >
                      <SectionGridItem {...tagGridSizes} label={t('common.tags')} testId={`tags`}>
                        {!!primarySimEntry?.tags?.items &&
                        primarySimEntry?.tags?.items.length > 0 ? (
                          <TagDisplay
                            id={primarySimEntry?.iccid}
                            initialTags={
                              primarySimEntry?.tags?.items.map((item: Tag) => item.name) || []
                            }
                            hasMore={primarySimEntry?.tags?.hasMore || false}
                            shouldWrap={true}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </SectionGridItem>
                    </SectionGridRow>
                  </>
                }
              />
            </SimProfileTitle>
          ) : (
            <ErrorFallback message={t('auth.somethingWrong')} />
          )}
        </PrimarySimGrid>
        <SecondarySimGrid sx={{ gridArea: 'secondary1' }} hideTopBorder={matchesMd}>
          {secondarySimEntry?.iccid && secondarySimEntry.iccid !== '-' ? (
            <>
              <SimProfileTitle>
                <Typography variant="h3" color="text.secondary">
                  {t('common.backup')}
                </Typography>
                <CopyableLink
                  value={secondarySimEntry?.iccid}
                  to={`${Routes.simInventory}/${secondarySimEntry?.iccid}`}
                />
              </SimProfileTitle>
              <SimProfileDetailsLoader
                iccid={secondarySimEntry?.iccid}
                data-testid={'secondarySimInventory'}
                rSimViewSimProfileRow={
                  <>
                    <SectionGridRow hideTopBorder={true} title={t('simInventory.simProfile')}>
                      <>
                        <SectionGridItem
                          {...simGridSizes}
                          label={t('simInventory.profileStatus')}
                          testId={`profileStatus`}
                        >
                          <RSimInventoryPollingSimStatus
                            euicc={euicc}
                            isPrimary={false}
                            rSimStatus={rSimStatus}
                          />
                        </SectionGridItem>
                        <SimInventoryCan I={Actions.simulate} a={Subjects.outage}>
                          <SectionGridItem
                            {...simGridSizes}
                            label={t('common.simulateOutage')}
                            testId={`simulateOutage`}
                          >
                            <RSimInventorySimulateOutage euicc={euicc} isPrimary={false} />
                          </SectionGridItem>
                        </SimInventoryCan>
                      </>
                    </SectionGridRow>
                    <SectionGridRow
                      hideTopBorder={true}
                      sx={{
                        pb: 12,
                      }}
                    >
                      <SectionGridItem {...tagGridSizes} label={t('common.tags')} testId={`tags`}>
                        {!!secondarySimEntry?.tags?.items &&
                        secondarySimEntry?.tags?.items.length > 0 ? (
                          <TagDisplay
                            id={secondarySimEntry?.iccid}
                            initialTags={
                              secondarySimEntry?.tags?.items.map((item: Tag) => item.name) || []
                            }
                            hasMore={secondarySimEntry?.tags?.hasMore || false}
                            shouldWrap={true}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </SectionGridItem>
                    </SectionGridRow>
                  </>
                }
              />
            </>
          ) : (
            <ErrorFallback message={t('auth.somethingWrong')} />
          )}
        </SecondarySimGrid>
      </Box>
    </SimProfileTagProvider>
  );
};
